import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Icon } from "@/lib/icons";
import type { PublicHomeCopy } from "./public-home-copy";
import { formatCount, formatShare, type LanguageTile, type LeaderRow } from "./public-home-data";
import { ErrorLine, HomeSection, SectionHeading, Waveform } from "./HomeParts";

const MAX_TILES = 12;

interface LanguageTilesProps {
  copy: PublicHomeCopy;
  lang: string;
  tiles: LanguageTile[] | null;
  isLoading: boolean;
}

/** One tile per collected language, biggest first. Hidden when the stats failed (the figures say so). */
export function LanguageTiles({ copy, lang, tiles, isLoading }: LanguageTilesProps) {
  const c = copy.languages;
  if (!tiles && !isLoading) return null;
  return (
    <HomeSection labelledBy="home-languages-title">
      <SectionHeading id="home-languages-title" eyebrow={c.eyebrow} title={c.title} />
      {tiles && tiles.length === 0 ? (
        <p className="mt-8 text-sm text-muted-foreground">{c.empty}</p>
      ) : (
        <ul className="mt-10 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
          {tiles
            ? tiles.slice(0, MAX_TILES).map((tile) => (
                <li key={tile.code} className="min-w-0 rounded-3xl border border-border bg-surface p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="min-w-0 truncate text-base font-semibold tracking-[-0.02em] text-foreground sm:text-lg">{tile.name}</h3>
                    <Waveform className="hidden h-6 w-12 shrink-0 sm:flex" bars={8} animated={false} />
                  </div>
                  <p className="mt-3 text-xl font-bold tabular-nums tracking-[-0.03em] text-foreground sm:mt-4 sm:text-2xl">
                    {formatCount(tile.total, lang)}
                    <span className="block text-xs font-normal tracking-normal text-muted-foreground sm:ml-1.5 sm:inline sm:text-sm">{c.collected}</span>
                  </p>
                  <div
                    className="mt-4 h-1.5 overflow-hidden rounded-full bg-muted"
                    role="img"
                    aria-label={c.validated(formatShare(tile.validatedShare, lang))}
                  >
                    <div className="h-full rounded-full bg-primary" style={{ width: `${Math.round(tile.validatedShare * 100)}%` }} />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">{c.validated(formatShare(tile.validatedShare, lang))}</p>
                </li>
              ))
            : Array.from({ length: 4 }, (_, i) => (
                <li key={i} className="rounded-3xl border border-border bg-surface p-4 sm:p-5">
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="mt-5 h-7 w-32" />
                  <Skeleton className="mt-5 h-1.5 w-full" />
                </li>
              ))}
        </ul>
      )}
    </HomeSection>
  );
}

interface WeeklyLeadersProps {
  copy: PublicHomeCopy;
  lang: string;
  rows: LeaderRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

export function WeeklyLeaders({ copy, lang, rows, isLoading, isError, onRetry }: WeeklyLeadersProps) {
  const c = copy.leaders;
  return (
    <HomeSection labelledBy="home-leaders-title">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
        <SectionHeading id="home-leaders-title" eyebrow={c.eyebrow} title={c.title}>
          <Link
            to={`/${lang}/leaderboard`}
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand underline-offset-4 hover:underline focus-ring"
          >
            {c.seeAll}
            <Icon name="arrowRight" className="h-3.5 w-3.5" />
          </Link>
        </SectionHeading>

        <div className="min-w-0">
          {rows && rows.length > 0 ? (
            <ol className="divide-y divide-border overflow-hidden rounded-3xl border border-border bg-surface">
              {rows.map((row) => (
                <li key={row.rank} className="flex items-center gap-4 px-4 py-4 sm:px-6">
                  <span className="w-6 shrink-0 text-sm font-semibold tabular-nums text-faint-foreground">{row.rank}</span>
                  <span
                    aria-hidden="true"
                    className={
                      row.rank === 1
                        ? "grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground"
                        : "grid h-10 w-10 shrink-0 place-items-center rounded-full border border-border bg-background text-sm font-semibold text-foreground"
                    }
                  >
                    {row.initial}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium text-foreground">{row.name}</span>
                  <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
                    {formatCount(row.contributions, lang)}
                    <span className="sr-only sm:not-sr-only"> {c.contributionsUnit}</span>
                  </span>
                </li>
              ))}
            </ol>
          ) : isError && !isLoading ? (
            <ErrorLine message={c.error} retryLabel={copy.figures.retry} onRetry={onRetry} />
          ) : rows && rows.length === 0 ? (
            <p className="rounded-3xl border border-dashed border-border px-6 py-10 text-center text-sm text-muted-foreground">{c.empty}</p>
          ) : (
            <div aria-busy="true" className="divide-y divide-border rounded-3xl border border-border bg-surface">
              {Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-4 sm:px-6">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </HomeSection>
  );
}
